import * as React from 'react';
import { cn } from '@shared/utils';

export type ThumbnailStatus = 'idle' | 'processing' | 'done' | 'error';

export interface ThumbnailProps {
  src: string;
  alt?: string;
  status?: ThumbnailStatus;
  onRemove?: () => void;
  className?: string;
}

function SpinnerIcon() {
  return (
    <svg
      className="h-8 w-8 animate-spin text-on-scrim"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function DoneIcon() {
  return (
    <svg
      className="h-8 w-8 text-on-scrim"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      className="h-8 w-8 text-on-scrim"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" opacity="0.25" fill="currentColor" />
      <rect x="11" y="7" width="2" height="8" rx="1" fill="currentColor" />
      <circle cx="12" cy="18" r="1" fill="currentColor" />
    </svg>
  );
}

function RemoveButton({ onRemove }: { onRemove: () => void }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label="移除照片"
      className={cn(
        'absolute right-1 top-1 flex h-[44px] w-[44px] items-center justify-center',
        'rounded-full bg-scrim/60 text-on-scrim/70',
        'transition-all duration-200 ease-out hover:bg-scrim/80 hover:text-on-scrim',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <svg
        className="h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>
  );
}

const statusOverlayClass: Record<ThumbnailStatus, string> = {
  idle: '',
  processing: 'bg-scrim/60',
  done: 'bg-success/30',
  error: 'bg-error/30',
};

/**
 * 方形縮圖，`idle` 以外的狀態會疊一層遮罩與對應圖示。
 *
 * 移除鍵**只在 `idle` 時出現**——其餘狀態即使傳了 `onRemove` 也不會顯示，
 * 呼叫端不必自己依狀態決定要不要傳。
 */
const Thumbnail = React.forwardRef<HTMLDivElement, ThumbnailProps>(
  ({ src, alt = '', status = 'idle', onRemove, className }, ref) => {
    const hasOverlay = status !== 'idle';

    return (
      <div
        ref={ref}
        className={cn('relative overflow-hidden rounded-md', className)}
        style={{ aspectRatio: '1 / 1' }}
      >
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover bg-muted"
          draggable={false}
        />

        {hasOverlay && (
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center',
              statusOverlayClass[status],
            )}
          >
            {status === 'processing' && <SpinnerIcon />}
            {status === 'done' && <DoneIcon />}
            {status === 'error' && <ErrorIcon />}
          </div>
        )}

        {onRemove && status === 'idle' && <RemoveButton onRemove={onRemove} />}
      </div>
    );
  },
);
Thumbnail.displayName = 'Thumbnail';

export { Thumbnail };
